import { Record } from 'immutable';

import { NOTIFY_ABOUT_CHANGED_LANG, NotifyAboutChangedLang } from 'app/lang';
import { TRANSFORM_SSR_STATE, TransformSSRState } from 'app/redux/ssrState';

import { transformMatchesSSRState } from './matches';
import updateStakeAndBetsNames from './matches/utils/update-stake-and-bets-names';
import { transformObjectsSSRState } from './objects';
import { transformSportsSSRState } from './sports';
import { transformStagesSSRState } from './stages';
import { transformTournamentsSSRState } from './tournaments';
import {
  BackendEntitiesWithDelta,
  BettingEntitiesState,
  CleanupActionData,
  CommonState,
  EntitiesReducers,
  EntityReducer,
  FailureActionData,
  MatchEntityReducer,
  RecordOfBettingEntitiesState,
  ReducersEntitiesNames,
  RequestActionData,
  SSRBettingEntitiesState,
  TriggerActionData,
} from './types/bettingRootReducer';
import { transformUnionsSSRState } from './unions';
import { getErrorMessage, getPayloadMatchIdFromTopic, isPrematchMatchesTopic, updatePrematchMatches } from './utils';

// ---

const SSR_STATE_TRANSFORMERS = {
  sports: transformSportsSSRState,
  tournamentUnions: transformUnionsSSRState,
  tournaments: transformTournamentsSSRState,
  tournamentStages: transformStagesSSRState,
  matches: transformMatchesSSRState,
  objects: transformObjectsSSRState,
};

type TriggerActions = RequestActionData | TriggerActionData | FailureActionData | CleanupActionData;
type TransformSSRAction = TransformSSRState<SSRBettingEntitiesState>;
type Actions = TransformSSRAction | TriggerActions | NotifyAboutChangedLang;

export type RootActions = {
  REQUEST_ACTION?: string;
  TRIGGER_ACTION?: string;
  FAILURE_ACTION?: string;
  CLEANUP_ACTION?: string;
};

type GetActionType<Action> = Action extends TransformSSRAction
  ? SSRBettingEntitiesState
  : (Action extends TriggerActions ? RecordOfBettingEntitiesState : unknown);

export default function rootReducerFactory(
  reducers: EntitiesReducers,
  {
    REQUEST_ACTION,
    TRIGGER_ACTION,
    FAILURE_ACTION,
    CLEANUP_ACTION,
  }: RootActions,
) {
  const entityNames = Object.keys(reducers) as ReducersEntitiesNames;

  type OnlyBettingEntitiesReducers = Omit<BettingEntitiesState, keyof CommonState>;

  const initialStateRecord = Record<BettingEntitiesState>(
    {
      delta: 0,
      error: undefined,
      signature: null,
      requested: false,
      loading: false,
      ...entityNames.reduce((acc, name) => {
        const reducer = reducers[name];

        if (name === 'matches') {
          const {
            matchesState,
            objectsState,
          } = (reducer as MatchEntityReducer)(undefined, undefined);

          return {
            ...acc,
            matches: matchesState,
            objects: objectsState,
          };
        }

        return {
          ...acc,
          [name]: (reducer as EntityReducer)(undefined, undefined),
        };
      }, {} as OnlyBettingEntitiesReducers),
    },
    'initialStateRecord',
  );

  const initialState = initialStateRecord();

  function isBettingState(state: unknown): state is RecordOfBettingEntitiesState {
    return state instanceof initialStateRecord;
  }

  return <A extends Actions>(
    state: GetActionType<A> | RecordOfBettingEntitiesState = initialState,
    action: A,
  ): (A extends Actions ? RecordOfBettingEntitiesState : unknown) => {
    switch (action.type) {
      case REQUEST_ACTION:
        if (!isBettingState(state)) {
          console.error(getErrorMessage(action.type, state));
          return initialStateRecord();
        }

        const actionProps = action as RequestActionData;

        return state.merge({
          loading: true,
          signature: actionProps.payload?.signature || null,
        });

      case TRIGGER_ACTION: {
        if (!isBettingState(state)) {
          console.error(getErrorMessage(action.type, state));
          return initialStateRecord();
        }

        const actionProps = action as TriggerActionData;

        if (isPrematchMatchesTopic(actionProps.payload?.topic) && actionProps.payload?.diff.reset) {
          const payloadMatchId = getPayloadMatchIdFromTopic(actionProps.payload?.topic);
          state = updatePrematchMatches(state, payloadMatchId);
        }
        else if (actionProps.payload?.diff.reset) {
          const signature = state.get('signature');

          (state as RecordOfBettingEntitiesState) = initialState.set('signature', signature);
        }

        (state as RecordOfBettingEntitiesState) = state.merge({
          requested: true,
          loading: false,
          delta: actionProps.payload.delta,
        });

        return receiveBettingEntitiesState(
          reducers,
          entityNames,
          state,
          actionProps.payload,
        );
      }

      case FAILURE_ACTION: {
        if (!isBettingState(state)) {
          console.error(getErrorMessage(action.type, state));
          return initialStateRecord();
        }

        return state.merge({
          requested: true,
          loading: false,
          error: (action as FailureActionData).error, // todo: handle error case...
        });
      }

      case TRANSFORM_SSR_STATE:
        if (Record.isRecord(state)) {
          return state as RecordOfBettingEntitiesState; // already transformed (initial state case)
        }

        const stateKeys = Object.keys(state) as (keyof SSRBettingEntitiesState)[];

        return initialStateRecord(
          stateKeys.map((key) => {
            let value = state[key];

            if (key in SSR_STATE_TRANSFORMERS) {
              value = SSR_STATE_TRANSFORMERS[
                key as keyof typeof SSR_STATE_TRANSFORMERS
              ](value);
            }

            return [key, value];
          }),
        );

      case NOTIFY_ABOUT_CHANGED_LANG: {
        const actionProps = action as NotifyAboutChangedLang;

        if (!isBettingState(state)) {
          console.error(getErrorMessage(actionProps.type, state));
          return initialStateRecord();
        }

        if (!state.has('matches')) {
          return state;
        }

        const objects = state.get('objects');

        return state.update('matches', (matches) => (
          matches.update('byId', (matchesById) => (
            updateStakeAndBetsNames(actionProps.payload.language, matchesById, objects, matches.get('allIds'))
          ))
        ));
      }

      case CLEANUP_ACTION: {
        if (!isBettingState(state)) {
          console.error(getErrorMessage(action.type, state));
          return initialStateRecord();
        }

        const actionProps = action as TriggerActionData;

        if (isPrematchMatchesTopic(actionProps.payload?.topic)) {
          const payloadMatchId = getPayloadMatchIdFromTopic(actionProps.payload?.topic);
          state = updatePrematchMatches(state, payloadMatchId);

          return state;
        }

        return initialState;
      }

      default:
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return state as any;
    }
  };
}

function receiveBettingEntitiesState(
  reducers: EntitiesReducers,
  entityNames: ReducersEntitiesNames,
  newState: RecordOfBettingEntitiesState,
  payload?: BackendEntitiesWithDelta,
): RecordOfBettingEntitiesState {
  return entityNames.reduce((state, name) => {
    const reducer = reducers[name];

    if (!reducer) {
      return state;
    }

    if (name === 'matches') {
      const matchesState = state.get('matches');
      const objectsState = state.get('objects');

      const matchesReducerState = (reducer as MatchEntityReducer)(
        { matchesState, objectsState },
        payload,
      );

      if (matchesReducerState.matchesState !== matchesState) {
        state = state.set('matches', matchesReducerState.matchesState);
      }
      if (matchesReducerState.objectsState !== objectsState) {
        state = state.set('objects', matchesReducerState.objectsState);
      }

      return state;
    }

    const prevState = state.get(name);

    const nextState = (reducer as EntityReducer)(
      prevState,
      payload,
      newState.get('matches')?.get('byId'),
    );

    if (nextState !== prevState) {
      state = state.set(name, nextState);
    }

    return state;
  }, newState);
}
